import { splitFolderTravelers, toNationalityCode } from '@koob/utils';
import { toDateString } from '@koob/dates';
import { ChildAgeModeEnum } from './types';
import type {
  ChildEntry,
  TravellerDetail,
  RoomPax,
  FolderFormValues,
} from './types';

export type { ChildEntry, TravellerDetail, RoomPax, FolderFormValues };

/** Whatever a date field stored → 'YYYY-MM-DD' day, '' when empty. */
const toDateOnlyValue = (value: unknown): string =>
  typeof value === 'string' || value instanceof Date
    ? (toDateString(value) ?? '')
    : '';

/**
 * Turn whatever the apps hand over — a freshly opened modal, a folder just read
 * from the API, or form values the app kept across a failed submit — into the
 * form's own shape.
 *
 * Two invariants the rest of this module relies on:
 *
 * - travellers are addressed positionally, adults first then children, so both
 *   queues are rebuilt from the folder's `kind`/`position` rather than trusted
 *   in the order the API returned them. Read in place, a folder holding
 *   travellers appended by a booking (no kind, no position) put an adult behind
 *   a child card, and its raw Date birthdate took the modal down with
 *   "Objects are not valid as a React child" (KD-3546);
 * - every date is a "YYYY-MM-DD" day. The apps' date fields store Date objects,
 *   which neither `<input type="date">` nor JSX can render.
 */
export function buildInitialFormValues(initialValues: any): FolderFormValues {
  const { adults: adultTravelers, children: childTravelers } =
    splitFolderTravelers(initialValues?.travelers);
  const hasTravelers = adultTravelers.length + childTravelers.length > 0;
  const initialRooms: any[] = initialValues?.roomsComposition ?? [];

  // A folder opened from a composed trip carries the trip's rooms but no
  // travellers yet: the pax counts follow the rooms rather than the 2-adult
  // default, so a solo trip does not open on a 2-adult folder.
  const adults =
    initialValues?.adults ??
    (hasTravelers
      ? adultTravelers.length
      : initialRooms.length
        ? initialRooms.reduce((s, r) => s + (r.adults ?? 0), 0)
        : 2);
  const children: ChildEntry[] =
    initialValues?.children ??
    (hasTravelers
      ? childTravelers.map((child: any) => ({
          mode:
            child.ageIsExact === false
              ? ChildAgeModeEnum.AGE
              : ChildAgeModeEnum.DATE,
          birthdate: toDateOnlyValue(child.birthdate) || null,
          age: child.age ?? null,
          ageIsExact: child.ageIsExact,
        }))
      : initialRooms.flatMap((room) =>
          Array.from({ length: room.children ?? 0 }, (_, i) => ({
            mode: ChildAgeModeEnum.DATE,
            birthdate: toDateOnlyValue(room.childrenBirthdates?.[i]) || null,
            age: null,
            ageIsExact: true,
          })),
        ));

  const rooms: RoomPax[] = initialRooms.length
    ? initialRooms.map((room: any) => ({
        adults: room.adults ?? 0,
        children: room.children ?? 0,
      }))
    : [{ adults, children: children.length }];

  return {
    id: initialValues?.id,
    name: initialValues?.name ?? '',
    icr: initialValues?.icr ?? '',
    locale: initialValues?.locale?.value ?? initialValues?.locale ?? 'en',
    // Left undefined when the caller has none: the key then drops out of the
    // payload and the API keeps what it stored, so a TO editing a DMC folder
    // cannot blank its Additional Details. The DMC's own empty form supplies
    // the empty strings.
    tourOperatorName: initialValues?.tourOperatorName,
    networkName: initialValues?.networkName,
    note: initialValues?.note,
    adults,
    children,
    rooms,
    travelers: [...adultTravelers, ...childTravelers].map(
      (traveller: any): TravellerDetail => ({
        ...traveller,
        birthdate: toDateOnlyValue(traveller?.birthdate) || undefined,
        passportExpirationDate:
          toDateOnlyValue(traveller?.passportExpirationDate) || undefined,
      }),
    ),
  };
}

/** "Under 18 years old" — the helper text under the Children stepper (KD-3546 §3.1). */
export const CHILD_MAX_AGE = 17;

/**
 * Coerce whatever the age input produced into a usable child age.
 *
 * A number input hands over everything the user types: `max` only constrains the
 * spinner arrows, so 20 goes straight through, and a lone "-" or "e" arrives as
 * NaN. The bound belongs to the data, not to the widget.
 */
export function normaliseChildAge(
  value: number | null | undefined,
): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  return Math.min(Math.max(Math.trunc(value), 0), CHILD_MAX_AGE);
}

export function estimateBirthdate(
  age: number,
  referenceDate: Date = new Date(),
): string {
  const year = referenceDate.getFullYear() - age;
  return `${year}-01-01`;
}

export function rebalanceRooms(
  adults: number,
  childrenCount: number,
  rooms: RoomPax[],
): RoomPax[] {
  const firstRoom = rooms[0] ?? { adults: 0, children: 0 };
  const otherRooms = rooms.slice(1);
  const otherAdults = otherRooms.reduce((s, r) => s + (r.adults ?? 0), 0);
  const otherChildren = otherRooms.reduce((s, r) => s + (r.children ?? 0), 0);

  const firstAdults = Math.max(0, adults - otherAdults);
  const firstChildren = Math.max(0, childrenCount - otherChildren);

  return [
    { ...firstRoom, adults: firstAdults, children: firstChildren },
    ...otherRooms,
  ];
}

/**
 * A traveller counts as complete once identity *and* birthdate are known: an
 * age-based estimate leaves the birthdate empty on the details side, so such a
 * traveller stays "to complete" until the exact date is confirmed.
 */
export function isTravellerComplete(tDetail?: TravellerDetail): boolean {
  if (!tDetail) return false;
  return Boolean(
    tDetail.firstName &&
    tDetail.firstName.trim() !== '' &&
    tDetail.lastName &&
    tDetail.lastName.trim() !== '' &&
    tDetail.gender &&
    toDateOnlyValue(tDetail.birthdate),
  );
}

/**
 * Options shared by canSave / getFooterHintKey.
 */
export interface FolderFormOptions {
  /**
   * Turns the traveller details section from optional into a prerequisite:
   * every pax must be complete before the folder can be saved. The DMC trip
   * proposal uses it, so the TO never receives a folder whose traveller
   * records are empty shells (KD-3546).
   */
  requireCompleteTravellers?: boolean;
}

/**
 * The traveller record as the details card reads it: a child's birthdate falls
 * back on the travellers step, where it may have been entered before the card
 * was ever opened. Completeness has to see the same date the card displays.
 */
export function effectiveTraveller(
  values: FolderFormValues,
  index: number,
): TravellerDetail | undefined {
  const detail = values.travelers[index];
  const childIndex = index - values.adults;

  if (childIndex < 0) return detail;

  const childBirthdate = values.children[childIndex]?.birthdate;
  if (toDateOnlyValue(detail?.birthdate) || !childBirthdate) return detail;

  return { ...(detail ?? {}), birthdate: childBirthdate };
}

/** How many of the folder's pax still miss identity or birthdate. */
export function countIncompleteTravellers(values: FolderFormValues): number {
  const totalPax = values.adults + values.children.length;
  const completedCount = Array.from({ length: totalPax }, (_, idx) =>
    isTravellerComplete(effectiveTraveller(values, idx)),
  ).filter(Boolean).length;

  return totalPax - completedCount;
}

export function canSave(
  values: FolderFormValues,
  { requireCompleteTravellers = false }: FolderFormOptions = {},
): { ok: boolean; reasonKey?: string } {
  const hasName = Boolean(values.name && values.name.trim() !== '');
  const hasIcr = Boolean(values.icr && values.icr.trim() !== '');

  if (!hasName && !hasIcr) {
    return { ok: false, reasonKey: 'bothRequired' };
  }
  if (!hasName) {
    return { ok: false, reasonKey: 'nameRequired' };
  }
  if (!hasIcr) {
    return { ok: false, reasonKey: 'icrRequired' };
  }
  if (!toFieldValue(values.locale)) {
    return { ok: false, reasonKey: 'languageRequired' };
  }
  if (values.adults < 1) {
    return { ok: false, reasonKey: 'adultRequired' };
  }

  const childInfoOk =
    values.children.length === 0 ||
    values.children.every((c) =>
      Boolean(c.birthdate && c.birthdate.trim() !== ''),
    );
  if (!childInfoOk) {
    return { ok: false, reasonKey: 'childBirthdateRequired' };
  }

  const sumAdults = values.rooms.reduce((s, r) => s + (r.adults ?? 0), 0);
  const sumChildren = values.rooms.reduce((s, r) => s + (r.children ?? 0), 0);

  if (sumAdults !== values.adults || sumChildren !== values.children.length) {
    return { ok: false, reasonKey: 'roomMismatch' };
  }

  if (requireCompleteTravellers && countIncompleteTravellers(values) > 0) {
    return { ok: false, reasonKey: 'travellersIncomplete' };
  }

  return { ok: true };
}

export function getFooterHintKey(
  values: FolderFormValues,
  { requireCompleteTravellers = false }: FolderFormOptions = {},
): { key: string; params?: Record<string, any> } {
  const hasName = Boolean(values.name && values.name.trim() !== '');
  const hasIcr = Boolean(values.icr && values.icr.trim() !== '');

  if (!hasName && !hasIcr) {
    return { key: 'footerHintBothRequired' };
  }
  if (!hasName) {
    return { key: 'footerHintNameRequired' };
  }
  if (!hasIcr) {
    return { key: 'footerHintIcrRequired' };
  }
  if (!toFieldValue(values.locale)) {
    return { key: 'footerHintLanguageRequired' };
  }
  if (values.adults < 1) {
    return { key: 'footerHintAdultRequired' };
  }

  const childInfoOk =
    values.children.length === 0 ||
    values.children.every((c) =>
      Boolean(c.birthdate && c.birthdate.trim() !== ''),
    );
  if (!childInfoOk) {
    return { key: 'footerHintChildBirthdateRequired' };
  }

  const sumAdults = values.rooms.reduce((s, r) => s + (r.adults ?? 0), 0);
  const sumChildren = values.rooms.reduce((s, r) => s + (r.children ?? 0), 0);

  if (sumAdults !== values.adults || sumChildren !== values.children.length) {
    return { key: 'footerHintRoomMismatch' };
  }

  const remaining = countIncompleteTravellers(values);

  if (remaining > 0) {
    return {
      key: requireCompleteTravellers
        ? 'footerHintTravellersRequired'
        : 'footerHintPendingTravellers',
      params: { remaining },
    };
  }

  return { key: 'saveHint' };
}

/**
 * A child's birthdate lives on both sides of the form: the travellers step (the
 * search data driving availability) and the traveller details card.
 *
 * The travellers step writes both sides itself, so the only direction left to
 * mirror is details -> search: a date typed on the details card confirms an
 * estimate, which permanently drops the approximation flag (KD-3546 §7).
 */
export interface ChildSyncSnapshot {
  /** travelers[adults + j].birthdate, normalised to a "YYYY-MM-DD" day */
  detail: string;
  /** children[j].birthdate */
  search: string;
}

export type ChildSyncAction =
  | { type: 'none' }
  | { type: 'confirmBirthdate'; entry: ChildEntry };

export function resolveChildSync(
  previous: ChildSyncSnapshot | undefined,
  current: ChildSyncSnapshot,
): ChildSyncAction {
  // First pass: nothing to mirror, only record where both sides stand.
  if (!previous) return { type: 'none' };

  // Untouched details card, or a value the travellers step just wrote on both
  // sides (it keeps them equal): nothing to do.
  if (current.detail === previous.detail) return { type: 'none' };
  if (!current.detail || current.detail === current.search) {
    return { type: 'none' };
  }

  return {
    type: 'confirmBirthdate',
    entry: {
      mode: ChildAgeModeEnum.DATE,
      birthdate: current.detail,
      age: null,
      ageIsExact: true,
    },
  };
}

/**
 * Traveller records are addressed by position — adults first, then children —
 * so changing the adults count would otherwise shift every child onto another
 * record. Re-index them so each traveller keeps its own details (KD-3546 §4).
 */
export function remapTravellers(
  travelers: TravellerDetail[],
  previousAdults: number,
  nextAdults: number,
  childrenCount: number,
): TravellerDetail[] {
  const keptAdults = Math.min(previousAdults, nextAdults);
  const adults = Array.from({ length: nextAdults }, (_, i) =>
    i < keptAdults ? (travelers[i] ?? {}) : {},
  );
  const children = Array.from(
    { length: childrenCount },
    (_, j) => travelers[previousAdults + j] ?? {},
  );

  return [...adults, ...children];
}

/**
 * Select fields do not agree on what they store: a plain code, an option
 * ({ value }) or a country ({ alpha2 }). The API only ever wants the code, so
 * both apps can hand their form values over untouched.
 */
export function toFieldValue(value: unknown): string | undefined {
  return toNationalityCode(value);
}

/** Traveller fields reach the API as plain codes and "YYYY-MM-DD" days. */
function normaliseTravellerFields(traveller: TravellerDetail) {
  const birthdate = toDateOnlyValue(traveller.birthdate);
  const passportExpirationDate = toDateOnlyValue(
    traveller.passportExpirationDate,
  );

  // The keys are always present, as undefined when empty: they are spread over
  // the raw traveller, whose date fields hold whatever the app's date pickers
  // stored — a Date object, or null once cleared. Omitting a key here left that
  // null in the payload, and `new Date(null)` on the API side is 1970-01-01.
  // JSON.stringify drops an undefined, so the field simply is not sent.
  return {
    birthdate: birthdate || undefined,
    passportExpirationDate: passportExpirationDate || undefined,
    gender: toFieldValue(traveller.gender),
    nationality: toFieldValue(traveller.nationality),
  };
}

export function buildFolderPayload(values: FolderFormValues) {
  // Children fill the rooms in order, and each room only carries the birthdates
  // of its own children: the API rejects a non-empty list whose length does not
  // match the room's children count. A room whose children are not all dated
  // sends an empty list, which stays valid while the information is missing.
  let assignedChildren = 0;
  const roomsComposition = values.rooms.map((r) => {
    const roomChildren = values.children.slice(
      assignedChildren,
      assignedChildren + r.children,
    );
    assignedChildren += r.children;

    const birthdates = roomChildren
      .map((c) => c.birthdate)
      .filter((b): b is string => Boolean(b));

    return {
      adults: r.adults,
      children: r.children,
      childrenBirthdates: birthdates.length === r.children ? birthdates : [],
    };
  });

  const travelers: TravellerDetail[] = [];

  // Adult travelers
  for (let i = 0; i < values.adults; i++) {
    const existing = values.travelers[i] ?? {};
    travelers.push({
      ...existing,
      kind: 'adult',
      position: i + 1,
      ...normaliseTravellerFields(existing),
    });
  }

  // Child travelers
  for (let j = 0; j < values.children.length; j++) {
    const child = values.children[j];
    const index = values.adults + j;
    const existing = values.travelers[index] ?? {};
    travelers.push({
      ...existing,
      kind: 'child',
      position: index + 1,
      ...normaliseTravellerFields(existing),
      birthdate:
        toDateOnlyValue(existing.birthdate) || child.birthdate || undefined,
      age: child.age ?? undefined,
      ageIsExact:
        existing.ageIsExact !== undefined
          ? existing.ageIsExact
          : child.ageIsExact,
    });
  }

  return {
    id: values.id,
    name: values.name,
    icr: values.icr,
    locale: values.locale,
    // The DMC's Additional Details ride along: the API assigns these three
    // unconditionally, so leaving them out of the payload blanks the stored
    // tour operator, network and notes (KD-3546).
    tourOperatorName: values.tourOperatorName,
    networkName: values.networkName,
    note: values.note,
    roomsComposition,
    travelers,
  };
}
